import {useSelector} from "react-redux";
import React, {useCallback, useEffect, useRef, useState} from "react";
import users from "../../users";
import {TitlePage} from "../../shared";
import auth from "../../../utils/authentication";
import {searchPage} from "../api/cap17drive-api";
import {useLocation, useNavigate} from "react-router-dom";
import {cn, normalize} from "../../../utils/utils";
import useSearchStore from './search-store';

import './cap17driveSearcher.scss';
import ItemsHorizontalScroll from "../shared/itemsHorizontalScroll/itemsHorizontalScroll";
import ItemTmdbBtnV2 from "../../shared/itemTmdbBtnV2/itemTmdbBtnV2";
import useSquadflixNavigate from "../squadflixHome/useSquadflixNavigate";
import {UserLocalStorageApi} from "../../../apis/local-storage/UserLocalStorageApi";

class SquadflixLocalSearchHistoryStore {

    static lastHistoryLoad = [];

    static async getSearchHistory() {
        try {
            const history = await UserLocalStorageApi.getItem({key: 'squadflix-search-history'});
            if (history.item && typeof history.item === 'object' && Array.isArray(history.item)) {
                this.lastHistoryLoad = history.item;
                return history.item;
            }
            return [];
        } catch (e) {
            console.error("Error getting search history:", e);
            return [];
        }
    }

    static async filterSearchHistory(term) {
        if (!this.lastHistoryLoad || this.lastHistoryLoad.length === 0) {
            await this.getSearchHistory();
        }
        const lowerTerm = term.toLowerCase();
        return this.lastHistoryLoad.filter(t => normalize(t).toLowerCase().includes(normalize(lowerTerm)));
    }

    static async addSearchTerm(term) {
        try {
            if (!term || term.trim() === '') {
                return;
            }
            let history = await SquadflixLocalSearchHistoryStore.getSearchHistory();
            history = history.filter(t => t !== term);
            history.unshift(term);
            if (history.length > 50) {
                history = history.slice(0, 50);
            }
            await UserLocalStorageApi.setItem({key: 'squadflix-search-history', value: history});
        } catch (e) {
            console.error("Error adding search term to history:", e);
        }
    }

    static clearHistory() {
        UserLocalStorageApi.removeItem({key: 'squadflix-search-history'}).catch(e => {
            console.error("Error clearing search history:", e);
        });
    }
}

const Cap17driveSearcher = ({
                                hideTitle,
                                navigateToSearch,
                                onlyResults,
                                onlyInput,
                                className,
                                showTmdbVotes = false,
                                onClickItem = undefined
                            }) => {

    const squadflixNav = useSquadflixNavigate({});
    const nameSearch = useSearchStore(state => state.nameSearch);
    const setNameSearch = useSearchStore(state => state.setNameSearch);
    const currentSearch = useSearchStore(state => state.currentSearch);
    const setCurrentSearch = useSearchStore(state => state.setCurrentSearch);
    const loading = useSearchStore(state => state.loading);
    const setLoading = useSearchStore(state => state.setLoading);
    const darkTheme = useSelector(users.selectors.isDarkMode);
    const hoveringOver = useSearchStore(state => state.hoveringOver);
    const location = useLocation();
    const inputRef = useRef(null);
    const [focusInput, setFocusInput] = useState(false);
    const [isInteractingWithMenu, setIsInteractingWithMenu] = useState(false);

    const [localInput, setLocalInput] = useState('');
    const navigate = useNavigate();
    const lastSearchDone = useRef(false);
    const autocompleteResultsDiv = useRef(null);
    const [selectedButtonIndex, setSelectedButtonIndex] = useState(0);
    const [localSearchHistory, setLocalSearchHistory] = useState([]);
    const [autocompletedResults, setAutocompletedResults] = useState([]);

    const navigateToSerie = (serie) => {
        if (onClickItem) {
            onClickItem({item: serie, isMovie: false});
            return;
        }
        squadflixNav.navigateToSerie(serie);
    }

    const navigateToMovie = (movie) => {
        if (onClickItem) {
            onClickItem({item: movie, isMovie: true});
            return;
        }
        squadflixNav.navigateToMovie(movie);
    }

    const makeSearch = useCallback(async (search, saveToHistory = false) => {
        if (!auth.currentUser) {
            return;
        }
        const useSearch = search ?? nameSearch;
        if (navigateToSearch && location.pathname !== "/squadflix/search-tmdb") {
            navigate('/squadflix/search-tmdb');
        }
        inputRef.current?.blur();
        if (currentSearch?.keywords !== useSearch) {
            setLoading(true);
            const moviesData = await searchPage(useSearch, 1, 'movies');
            const seriesData = await searchPage(useSearch, 1, 'series');
            const data = {
                keywords: useSearch,
                movies: moviesData?.results || [],
                series: seriesData?.results || []
            };
            setCurrentSearch(data);
            localStorage.setItem('squadflix-last-search', useSearch);
            if (saveToHistory) {
                SquadflixLocalSearchHistoryStore.addSearchTerm(useSearch);
            }
            setLoading(false);
        }
    }, [nameSearch, navigateToSearch, location.pathname, currentSearch?.keywords, navigate, setLoading, setCurrentSearch]);

    useEffect(() => {
        SquadflixLocalSearchHistoryStore.lastHistoryLoad = [];
    }, []);

    useEffect(() => {
        const lastSearch = localStorage.getItem("squadflix-last-search");
        if (lastSearchDone.current || onlyInput) {
            return;
        }
        lastSearchDone.current = true;
        if (lastSearch) {
            setNameSearch(lastSearch);
            makeSearch(lastSearch);
        }
    }, [makeSearch, onlyInput, setNameSearch]);

    useEffect(() => {

        if (!localInput?.length || !focusInput) {
            setAutocompletedResults([]);
            return;
        }

        const makeSearchDebounced = setTimeout(async () => {
            const moviesData = await searchPage(localInput, 1, 'movies');
            const seriesData = await searchPage(localInput, 1, 'series');

            const items = [];
            if (moviesData?.results && moviesData.results.length > 0) {
                items.push(moviesData.results[0]);
            }
            if (seriesData?.results && seriesData.results.length > 0) {
                items.push(seriesData.results[0]);
            }
            setAutocompletedResults(items);
        }, 500);

        return () => clearTimeout(makeSearchDebounced);

    }, [localInput, focusInput]);

    const lauchedFirstFocus = useRef(false);
    useEffect(() => {
        if (!onlyInput || lauchedFirstFocus.current) {
            if (location.pathname !== "/squadflix/search-tmdb") {
                lauchedFirstFocus.current = false;
            }
            return;
        }


        if (location.pathname === "/squadflix/search-tmdb" && inputRef.current) {
            lauchedFirstFocus.current = true;
            inputRef.current?.focus();
        } else {
            lauchedFirstFocus.current = false;
        }
    }, [location, onlyInput, inputRef.current]);

    const controlKey = event => {
        if (event.key === "Enter") {
            let blurInput = false;
            if (selectedButtonIndex >= 0 && autocompleteResultsDiv.current) {
                const btn = autocompleteResultsDiv.current.children[selectedButtonIndex];
                if (btn) {
                    btn.click();
                    blurInput = true;
                }
            } else if (nameSearch) {
                makeSearch(undefined, true);
                blurInput = true;
            }
            if (blurInput) {
                setTimeout(() => {
                    inputRef.current?.blur();
                    setFocusInput(false);
                }, 30);
            }
        } else if (event.key === "Escape") {
            inputRef.current?.blur();
        } else if (event.key === "ArrowDown") {
            event.preventDefault();
            if (selectedButtonIndex < (autocompleteResultsDiv.current?.children.length ?? 0) - 1) {
                setSelectedButtonIndex(selectedButtonIndex + 1);
                const btn = autocompleteResultsDiv.current?.children[selectedButtonIndex + 1];
                btn?.scrollIntoView({block: "nearest"});
            }
        } else if (event.key === "ArrowUp") {
            event.preventDefault();
            if (selectedButtonIndex > 0) {
                setSelectedButtonIndex(selectedButtonIndex - 1);
                const btn = autocompleteResultsDiv.current?.children[selectedButtonIndex - 1];
                btn?.scrollIntoView({block: "nearest"});
            }
        }
    }

    useEffect(() => {
        if (onlyResults) return;

        const handleShortcut = (e) => {
            if ((e.key === 'f' || e.key === 'F') && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                setFocusInput(true);
                setTimeout(() => {
                    inputRef.current?.focus();
                }, 100);
            }
        }

        window.addEventListener('keydown', handleShortcut);
        return () => {
            window.removeEventListener('keydown', handleShortcut);
        }

    }, [onlyResults]);

    useEffect(() => {
        const hoverCard = document.getElementById('cap17driveSearcherHoverCard');
        if (hoveringOver && hoverCard) {
            const rect = document.getElementById(hoveringOver.id).getBoundingClientRect();
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
            let top = rect.top + scrollTop - hoverCard.offsetHeight - 10;
            let left = rect.left + scrollLeft + (rect.width / 2) - (hoverCard.offsetWidth / 2);

            if (top < scrollTop) {
                top = rect.bottom + scrollTop + 10;
            }
            if (left < scrollLeft) {
                left = scrollLeft + 10;
            } else if ((left + hoverCard.offsetWidth) > (scrollLeft + window.innerWidth)) {
                left = scrollLeft + window.innerWidth - hoverCard.offsetWidth - 10;
            }

            hoverCard.style.top = `${top}px`;
            hoverCard.style.left = `${left}px`;
            hoverCard.style.opacity = 1;
        } else if (hoverCard) {
            hoverCard.style.opacity = 0;
        }
    }, [hoveringOver]);

    useEffect(() => {
        if (!focusInput) {
            return;
        }
        setSelectedButtonIndex(0);
        SquadflixLocalSearchHistoryStore.filterSearchHistory(localInput).then(filteredHistory => {
            setLocalSearchHistory(filteredHistory);
        });
    }, [focusInput, localInput]);

    const getVoteAverage = (item) => {
        if (!showTmdbVotes) {
            return null;
        }
        if (item.vote_average == undefined) {
            return null;
        }

        return (<>
            {item.vote_average.toFixed(1)}
            <i className="fa-solid fa-star fa-sm ms-1 text-warning"></i>
        </>)
    }

    return (
        <div
            className={cn(`d-flex d-column justify-content-center flex-wrap myDrive__container ${className ?? ""}`, {"cap17driveSearcher--light": !darkTheme})}
            id="cap17driveSearcher">
            {!onlyResults &&
                <div className="cap17driveSearcher__input-container"
                     onClick={() => {
                         inputRef.current?.focus();
                     }}
                     style={{
                         paddingTop: hideTitle ? "17px" : "0px",
                         backgroundColor: onlyInput ? 'transparent' : (darkTheme ? 'rgb(33,37,41)' : '#ffffff'),
                     }}>
                    <TitlePage icon="fa-solid fa-magnifying-glass"
                               leftClass={'col-12'}
                               disableScroll
                               title={
                                   <div className="position-relative w-100">
                                       <input placeholder="Buscar en SquadFlix"
                                              type="text"
                                              onFocus={() => setFocusInput(true)}
                                              onBlur={() => {
                                                  // Solo ocultar si no se está interactuando con el menú
                                                  if (!isInteractingWithMenu) {
                                                      setTimeout(() => {
                                                          setFocusInput(false);
                                                      }, 30);
                                                  }
                                              }}
                                              ref={inputRef}
                                              className={cn("cap17driveSearcher__search-input")}
                                              id="squadFlixSearchInput" value={localInput}
                                              onChange={e => {
                                                  const v = e.target.value;
                                                  setLocalInput(v);
                                                  setNameSearch(v);
                                              }}
                                              autoComplete="off"
                                              onKeyDown={e => {
                                                  controlKey(e);
                                              }}/>
                                       {(localInput && localInput.length > 0) &&
                                           <button className="cap17driveSearcher__clear-input"
                                                   onClick={() => {
                                                       setLocalInput('');
                                                       inputRef.current?.focus();
                                                   }}>
                                               <i className="fa-solid fa-xmark"></i>
                                           </button>}
                                   </div>
                               }/>
                    <div className="cap17driveSearcher__autocomplete-results"
                         ref={autocompleteResultsDiv}
                         style={{
                             top: (focusInput) ? "60px" : "-999px",
                             opacity: (focusInput) ? 1 : 0,
                         }}
                         onMouseDown={() => setIsInteractingWithMenu(true)}
                         onTouchStart={() => setIsInteractingWithMenu(true)}
                         onMouseUp={() => {
                             setIsInteractingWithMenu(false);
                             setTimeout(() => {
                                 setFocusInput(false);
                             }, 30);
                         }}
                         onPointerUp={() => {
                             setIsInteractingWithMenu(false);
                             setTimeout(() => {
                                 inputRef.current?.blur();
                                 setFocusInput(false);
                             }, 30);
                         }}
                    >
                        <button type="button" hidden={!localInput || localInput.trim() === ''}
                                className={cn("cap17driveSearcher__autocomplete-results-btn", {selected: selectedButtonIndex === 0})}
                                disabled={!auth.currentUser}
                                onClick={() => {
                                    if (!auth.currentUser) {
                                        return;
                                    }
                                    makeSearch(undefined, true);
                                    setFocusInput(false);
                                }}>
                            <i className="fa-solid fa-magnifying-glass pe-2"></i>
                            "{localInput}"
                        </button>
                        <button type="button"
                                className={cn("cap17driveSearcher__autocomplete-results-btn", {selected: selectedButtonIndex === 1})}
                                onClick={() => {
                                    inputRef.current?.blur();
                                    setFocusInput(false);
                                }}>
                            <i className="fa-solid fa-xmark pe-2"></i>
                            Cancelar
                        </button>
                        {autocompletedResults.map((item, index) => (
                            <button type="button"
                                    className={cn("cap17driveSearcher__autocomplete-results-btn", {selected: selectedButtonIndex === index + 2})}
                                    key={item.id}
                                    onClick={() => {
                                        setLocalInput(item.title || item.name);
                                        setNameSearch(item.title || item.name);
                                        if (item.isMovie) {
                                            navigateToMovie(item);
                                        } else {
                                            navigateToSerie(item);
                                        }
                                        setFocusInput(false);
                                    }}>
                                {item.isMovie && <i className="fa-solid fa-film pe-2"></i>}
                                {!item.isMovie && <i className="fa-solid fa-tv pe-2"></i>}
                                {item.title || item.name}
                            </button>
                        ))}
                        {localSearchHistory.map((term, index) => (
                            <button type="button"
                                    className={cn("cap17driveSearcher__autocomplete-results-btn", {selected: selectedButtonIndex === index + autocompletedResults.length + 2})}
                                    key={index}
                                    onClick={() => {
                                        setLocalInput(term);
                                        setNameSearch(term);
                                        makeSearch(term);
                                        setFocusInput(false);
                                    }}>
                                <i className="fa-solid fa-clock-rotate-left pe-2"></i>
                                {term}
                            </button>
                        ))}
                        {localSearchHistory.length > 0 &&
                            <button type="button"
                                    className={cn("cap17driveSearcher__autocomplete-results-btn", {selected: selectedButtonIndex === localSearchHistory.length + autocompletedResults.length + 2})}
                                    onClick={() => {
                                        SquadflixLocalSearchHistoryStore.clearHistory();
                                    }}>
                                <i className="fa-solid fa-trash-can pe-2"></i>
                                Limpiar historial
                            </button>}
                    </div>
                </div>}
            {!onlyInput && <>
                {currentSearch.series.length > 0 || currentSearch.movies.length > 0 ?
                    <div className="cap17driveSearcher__content">
                        <div className="fs-6 mb-3 text-muted" style={{fontWeight: 500}}>
                            {`Resultados para "${currentSearch.keywords}"`}
                        </div>
                        <div className="cap17driveSearcher__results">
                            {!!currentSearch.movies?.length && <div>
                                <TitlePage icon="fa-solid fa-film" title="Peliculas" leftClass="col-12" hideSeparator/>
                                <ItemsHorizontalScroll className="cap17driveSearcher__results-grid">
                                    {
                                        currentSearch.movies.map(movie => (
                                            <ItemTmdbBtnV2 description={movie.release_date} key={movie.id}
                                                           bottomInfo={getVoteAverage(movie)}
                                                           tmdbItem={movie} type={'tv'}
                                                           onClick={navigateToMovie}/>
                                        ))
                                    }
                                </ItemsHorizontalScroll>
                            </div>}
                            {!!currentSearch.series?.length && <div>
                                <TitlePage icon="fa-solid fa-tv" title="Series" leftClass="col-12"
                                           hideSeparator/>
                                <ItemsHorizontalScroll className="cap17driveSearcher__results-grid">
                                    {
                                        currentSearch.series.map(movie => (
                                            <ItemTmdbBtnV2 description={movie.first_air_date} key={movie.id}
                                                           bottomInfo={getVoteAverage(movie)}
                                                           tmdbItem={movie} type={'movie'}
                                                           onClick={navigateToSerie}/>
                                        ))
                                    }
                                </ItemsHorizontalScroll>
                            </div>}
                        </div>
                    </div>

                    :
                    (loading ?
                        <div className="text-center my-5">
                            <div className="spinner-border text-secondary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                        :
                        currentSearch.keywords &&
                        <div className="text-center my-5">
                            <h3>No se han encontrado resultados para "{currentSearch.keywords}"</h3>
                            <i className="fa-solid fa-magnifying-glass fa-2xl"></i>
                        </div>)
                }
            </>}
        </div>
    );
}

export default Cap17driveSearcher;
